import { useLayoutEffect, useRef, type KeyboardEvent } from 'react';

/** 터치 키보드에는 Shift+Enter 가 없다 — 거기서 Enter 는 줄바꿈으로 두고 보내기는 버튼이 맡는다 */
const touchKeyboard = () => typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

/**
 * 입력칸에서 Enter 는 보내기, Shift+Enter 는 줄바꿈.
 * - 한글·일본어 조합 중의 Enter 는 글자 확정이라 보내지 않는다 — Safari 는 조합이 끝난 직후의 Enter 를 isComposing 없이 keyCode 229 로 준다
 * - Enter 를 누르고 있어서 반복되는 입력은 보내지 않는다 (같은 글이 응답마다 다시 나가 이용권이 빠진다)
 * - 응답을 받는 동안 입력칸이 disabled 가 되어 포커스가 빠진다 — Enter 로 보냈으면 응답이 끝날 때 입력칸으로 되돌린다.
 *   잠금이 풀리는 같은 커밋에서 되돌린다(useLayoutEffect) — useEffect 면 풀린 뒤 한 박자 동안 포커스가 없다
 *
 * send 는 실제로 보냈으면 true 를 돌려준다. busy 는 입력칸이 잠기는 조건과 같은 값이다
 */
export function useSendOnEnter(send: () => boolean, busy: boolean) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const refocus = useRef(false);
  useLayoutEffect(() => {
    if (busy || !refocus.current) return;
    refocus.current = false;
    ref.current?.focus();
  }, [busy]);
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (touchKeyboard()) return;
    event.preventDefault();
    if (event.repeat) return;
    if (send()) refocus.current = true;
  };
  return { ref, onKeyDown };
}
